import { Pipe, PipeTransform } from '@angular/core';

function formatFiniteNumber(n: number): string {
	if (!Number.isFinite(n)) {
		return '';
	}
	if (Object.is(n, -0)) {
		return '0';
	}
	const abs = Math.abs(n);
	if (abs >= 1e15 || (abs > 0 && abs < 1e-12)) {
		return n.toExponential(4);
	}
	const nearInt = Math.round(n);
	if (Math.abs(n - nearInt) < 1e-9) {
		return nearInt.toLocaleString(undefined, { useGrouping: true, maximumFractionDigits: 0 });
	}
	const tidied = Number.parseFloat(n.toPrecision(14));
	const v = Number.isFinite(tidied) ? tidied : n;
	return v.toLocaleString(undefined, {
		maximumFractionDigits: 6,
		minimumFractionDigits: 0,
		useGrouping: true,
	});
}

/** Column title includes % — treat small decimals as ratios (0.234 → 23.4%), larger values as already percents. */
function percentStyleColumn(header: string | null | undefined): boolean {
	if (!header) {
		return false;
	}
	return String(header).includes('%');
}

function formatWithColumnHint(n: number, columnHeader: string | null | undefined): string {
	if (percentStyleColumn(columnHeader)) {
		const abs = Math.abs(n);
		if (abs <= 1 && abs > 1e-15) {
			const pct = n * 100;
			return `${pct.toLocaleString(undefined, { maximumFractionDigits: 4, minimumFractionDigits: 0, useGrouping: true })}%`;
		}
		if (abs > 1 && abs <= 500) {
			return `${formatFiniteNumber(n)}%`;
		}
	}
	return formatFiniteNumber(n);
}

@Pipe({
	name: 'pricebookCellFormat',
	pure: true,
})
export class PricebookCellFormatPipe implements PipeTransform {
	transform(value: unknown, columnHeader?: string | null): string {
		if (value === null || value === undefined) {
			return '';
		}
		if (typeof value === 'boolean') {
			return value ? 'Yes' : 'No';
		}
		if (typeof value === 'number' && Number.isFinite(value)) {
			return formatWithColumnHint(value, columnHeader);
		}
		const raw = String(value).trim();
		if (raw === '') {
			return '';
		}
		const normalized = raw.replace(/,/g, '');
		const asNum = Number(normalized);
		const looksNumeric =
			/^[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?$/.test(normalized) &&
			Number.isFinite(asNum) &&
			normalized !== '-';

		const digitRun = normalized.replace(/^-/, '');
		const isIntegerLike = looksNumeric && !raw.includes('.') && !/[eE]/.test(raw);
		if (isIntegerLike && digitRun.length >= 7) {
			return raw;
		}

		if (looksNumeric && (raw.includes('.') || /[eE]/.test(raw) || raw.includes(','))) {
			return formatWithColumnHint(asNum, columnHeader);
		}

		const max = 120;
		return raw.length > max ? `${raw.slice(0, max)}…` : raw;
	}
}

@Pipe({
	name: 'pricebookCellTitle',
	pure: true,
})
export class PricebookCellTitlePipe implements PipeTransform {
	transform(value: unknown): string {
		if (value === null || value === undefined) {
			return '';
		}
		const s = String(value);
		return s.length > 480 ? `${s.slice(0, 480)}…` : s;
	}
}
